import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const StructureLesson = ({ onNextLesson, markLessonComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState(Array(4).fill(false));

  const questions = [
    {
      question: 'How many cards are there in a standard Tarot deck?',
      options: ['78', '56', '22', '100'],
      correctAnswer: '78',
    },
    {
      question: 'How many suits are there in the Minor Arcana?',
      options: ['2', '3', '4', '5'],
      correctAnswer: '4',
    },
    {
      question: 'What is the primary focus of the Major Arcana cards?',
      options: ['Daily activities', 'Significant life events', 'Weather predictions', 'Dream interpretations'],
      correctAnswer: 'Significant life events',
    },
    {
      question: 'What is the primary focus of the Minor Arcana cards?',
      options: ['Material aspects and career', 'Significant life events', 'Spiritual lessons', 'Astrological signs'],
      correctAnswer: 'Material aspects and career',
    },
  ];

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = () => {
    if (selectedOption === questions[currentQuestion].correctAnswer) {
      setFeedback('Correct!');
      // Mark this question as completed
      setCompletedQuestions(prev => {
        const newCompleted = [...prev];
        newCompleted[currentQuestion] = true;
        return newCompleted;
      });
      setTimeout(() => {
        setSelectedOption('');
        setFeedback(null);
        if (currentQuestion + 1 < questions.length) {
          setCurrentQuestion(currentQuestion + 1);
        } else {
          setAllQuestionsAnsweredCorrectly(true);
        }
      }, 2000);
    } else {
      setFeedback(`Incorrect. The correct answer is: ${questions[currentQuestion].correctAnswer}`);
      setTimeout(() => {
        setSelectedOption('');
        setFeedback(null);
      }, 4000);
    }
  };

  const handleQuestionChange = (newQuestionIndex) => {
    if (newQuestionIndex >= 0 && newQuestionIndex < questions.length) {
      setCurrentQuestion(newQuestionIndex);
      setSelectedOption('');
      setFeedback(null);
    }
  };

  const handleDotClick = (index) => {
    if (index <= currentQuestion || completedQuestions[index]) {
      handleQuestionChange(index);
    }
  };

  return (
    <div className="container">
      <h1 className="title has-text-warning">Structure of the Tarot Deck</h1>

      <section className="section has-text-centered" id="structure">
        <div className="container content">
          <div className="columns">
            <div className="column">
              <div className="content has-text-warning">
                <p className="is-italic">
                  Welcome to this Tarot Course. In the first lesson, you will understand the structure of the deck.
                </p>
                <p>
                  A standard Tarot deck comprises 78 cards, divided into two primary sections: the Major Arcana and the Minor Arcana.
                </p>
                <p>
                  The Major Arcana consists of 22 cards, each representing significant life events, spiritual lessons, and archetypal energies. These cards are the pillars of the Tarot, guiding us through the major milestones of our spiritual journey.
                </p>
                <p>
                  The Minor Arcana, on the other hand, consists of 56 cards divided into four suits:
                </p>
                <ul>
                  <li>Cups: represent emotions and relationships</li>
                  <li>Pentacles: symbolize material aspects and career</li>
                  <li>Swords: denote intellect and conflict</li>
                  <li>Wands: signify creativity and action</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container content">
        <div className="columns">
          <div className="column">
            <h2 className="has-text-primary">Did you pay attention?</h2>
            <p className="has-text-white">{questions[currentQuestion].question}</p>
            <div className="is-flex is-align-items-center is-justify-content-center">
              <div className="select mr-2">
                <select value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
                  <option value="" disabled>Select an option</option>
                  {questions[currentQuestion].options.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <button className="button is-warning" onClick={handleAnswer}>
                Submit
              </button>
            </div>
            {feedback && <p className="mt-2 has-text-warning">{feedback}</p>}
          </div>
        </div>
      </div>

      <section className="section has-text-centered" style={{ paddingBottom: '1rem' }}>
        <div className="column">
          {/* Navigation is now handled by CourseHero component */}
        </div>
      </section>
    </div>
  );
};

export default StructureLesson;
